/**
 * 스토리 테스트 사운드.
 * 음원 파일 없이 Web Audio API 로 배경음(빗소리·방·밤·카페)과 효과음을 합성합니다.
 * 테스트 데이터의 sounds: { 이름: mp3주소 } 로 mp3 를 연결하면 그 이름이 우선합니다.
 *
 * 브라우저 정책상 첫 탭(사용자 동작) 이후에만 소리를 낼 수 있어 start() 를 그때 호출합니다.
 */

const MUTE_KEY = "mango-story-muted";
const FADE = 1.6; // 배경음 전환(초)

const readMuted = () => {
  try {
    return window.localStorage.getItem(MUTE_KEY) === "1";
  } catch {
    return false;
  }
};

const noiseBuffer = (ctx, seconds, color) => {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  let last = 0;
  for (let i = 0; i < len; i++) {
    const white = Math.random() * 2 - 1;
    if (color === "brown") {
      last = (last + 0.02 * white) / 1.02;
      d[i] = last * 3.5;
    } else {
      d[i] = white;
    }
  }
  return buf;
};

/* 픽셀 모드용 칩튠: 장면 분위기별 프리셋 (아주 작은 볼륨) */
const CHIP = {
  // 밤, 내 방: 느긋하고 포근하게
  night: {
    bpm: 74,
    root: 60,
    prog: [[0, 4, 7], [-3, 0, 4], [-7, -3, 0], [-5, -1, 2]],
    melody: [7, null, 9, null, 12, null, 9, 7, 4, null, null, null, 2, 4, null, null,
      7, null, 9, null, 12, 14, 12, 9, 7, null, null, null, null, null, null, null],
  },
  // 아침, 맨 정신: 살짝 쓸쓸하게
  morning: {
    bpm: 66,
    root: 57,
    prog: [[0, 3, 7], [-4, 0, 3], [-7, -4, 0], [-5, -2, 2]],
    melody: [12, null, null, 10, null, 7, null, null, 3, null, 5, null, 7, null, null, null,
      12, null, null, 15, null, 14, 12, null, 10, null, null, null, null, null, null, null],
  },
  // 카페: 조금 더 통통 튀게
  cafe: {
    bpm: 96,
    root: 62,
    prog: [[0, 4, 7, 11], [5, 9, 12, 16], [2, 5, 9, 12], [7, 11, 14, 17]],
    melody: [12, 14, 16, null, 14, 12, 9, null, 7, 9, null, 12, null, null, 9, null,
      12, 14, 16, 19, 16, 14, 12, null, 14, null, 12, null, null, null, null, null],
  },
};
const CHIP_FOR = { 방: "night", 밤: "night", 빗소리: "night", 새벽: "night", 사무실: "morning", 아침: "morning", 카페: "cafe" };
const mtof = (m) => 440 * Math.pow(2, (m - 69) / 12);

export class StorySound {
  constructor(files = {}, { chip = false } = {}) {
    this.files = files;
    this.chip = chip; // 픽셀 모드: 배경음을 칩튠으로, 타자 소리를 블립으로
    this.ctx = null;
    this.muted = readMuted();
    this.current = null; // { name, gain, stop }
    this.wanted = null;
  }

  /** 첫 탭에서 호출 */
  start() {
    if (this.ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.muted ? 0 : 1;
    this.master.connect(this.ctx.destination);
    this.white = noiseBuffer(this.ctx, 2, "white");
    this.brown = noiseBuffer(this.ctx, 4, "brown");
    if (this.wanted) this.music(this.wanted);
  }

  setMuted(muted) {
    this.muted = muted;
    try {
      window.localStorage.setItem(MUTE_KEY, muted ? "1" : "0");
    } catch {
      /* 저장 불가 환경은 무시 */
    }
    if (this.ctx) {
      const t = this.ctx.currentTime;
      this.master.gain.cancelScheduledValues(t);
      this.master.gain.setTargetAtTime(muted ? 0 : 1, t, 0.15);
    }
  }

  dispose() {
    if (this.current) this.current.stop(0);
    if (this.ctx) this.ctx.close().catch(() => {});
    this.ctx = null;
  }

  /* ---------- 배경음 ---------- */

  music(name) {
    this.wanted = name;
    if (!this.ctx) return;
    if (this.current && this.current.name === name) return;
    const t = this.ctx.currentTime;
    if (this.current) {
      const old = this.current;
      old.gain.gain.setTargetAtTime(0, t, FADE / 3);
      old.stop(FADE * 2);
      this.current = null;
    }
    if (!name || name === "끔") return;

    const gain = this.ctx.createGain();
    gain.gain.value = 0;
    gain.connect(this.master);
    const stop = this.buildAmbience(name, gain);
    if (!stop) return;
    gain.gain.setTargetAtTime(1, t, FADE / 3);
    this.current = { name, gain, stop };
  }

  buildAmbience(name, out) {
    const ctx = this.ctx;
    const nodes = [];
    const timers = [];
    const loop = (buffer) => {
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.loop = true;
      src.start();
      nodes.push(src);
      return src;
    };
    const filter = (type, freq, q = 0.7) => {
      const f = ctx.createBiquadFilter();
      f.type = type;
      f.frequency.value = freq;
      f.Q.value = q;
      return f;
    };
    const level = (v) => {
      const g = ctx.createGain();
      g.gain.value = v;
      return g;
    };
    const stopNow = () => {
      timers.forEach(clearInterval);
      nodes.forEach((n) => {
        try {
          n.stop();
        } catch {
          /* 이미 멈춤 */
        }
      });
      out.disconnect();
    };
    const stopAll = (delay = 0) => {
      if (delay <= 0) stopNow();
      else setTimeout(stopNow, delay * 1000);
    };

    if (this.files[name]) {
      const audio = new Audio(this.files[name]);
      audio.loop = true;
      audio.crossOrigin = "anonymous";
      const src = ctx.createMediaElementSource(audio);
      src.connect(out);
      audio.play().catch(() => {});
      return (delay = 0) =>
        setTimeout(() => {
          audio.pause();
          out.disconnect();
        }, delay * 1000);
    }

    if (this.chip) {
      this.chiptune(CHIP[CHIP_FOR[name]] || CHIP.night, out, timers);
      return stopAll;
    }

    switch (name) {
      case "빗소리": {
        // 넓은 빗소리 + 가끔 떨어지는 물방울
        const body = loop(this.white);
        const hp = filter("highpass", 400);
        const lp = filter("lowpass", 5200);
        body.connect(hp).connect(lp).connect(level(0.11)).connect(out);
        const low = loop(this.brown);
        low.connect(filter("lowpass", 500)).connect(level(0.25)).connect(out);
        timers.push(
          setInterval(() => {
            if (Math.random() < 0.55) this.drop(out);
          }, 140)
        );
        break;
      }
      case "방": {
        // 조용한 방: 낮은 웅웅거림 + 냉장고 같은 험
        const room = loop(this.brown);
        room.connect(filter("lowpass", 260)).connect(level(0.32)).connect(out);
        const hum = ctx.createOscillator();
        hum.frequency.value = 58;
        hum.connect(level(0.012)).connect(out);
        hum.start();
        nodes.push(hum);
        break;
      }
      case "카페": {
        // 웅성거림: 대역 제한 잡음이 천천히 출렁임
        const crowd = loop(this.brown);
        const bp = filter("bandpass", 700, 0.6);
        const g = level(0.35);
        crowd.connect(bp).connect(g).connect(out);
        const lfo = ctx.createOscillator();
        lfo.frequency.value = 0.23;
        const lfoAmt = level(0.12);
        lfo.connect(lfoAmt).connect(g.gain);
        lfo.start();
        nodes.push(lfo);
        timers.push(
          setInterval(() => {
            if (Math.random() < 0.18) this.clink(out);
          }, 900)
        );
        break;
      }
      case "밤":
      default: {
        // 잔잔한 패드: 살짝 어긋난 두 화음이 천천히 숨쉬듯
        [110, 164.8, 220.6].forEach((f, i) => {
          const o = ctx.createOscillator();
          o.type = "sine";
          o.frequency.value = f;
          o.detune.value = (i - 1) * 6;
          const g = level(0.0);
          o.connect(g).connect(out);
          const lfo = ctx.createOscillator();
          lfo.frequency.value = 0.05 + i * 0.03;
          const amt = level(0.022);
          lfo.connect(amt).connect(g.gain);
          g.gain.value = 0.03;
          o.start();
          lfo.start();
          nodes.push(o, lfo);
        });
        const air = loop(this.brown);
        air.connect(filter("lowpass", 900)).connect(level(0.06)).connect(out);
      }
    }
    return stopAll;
  }

  chiptune(p, out, timers) {
    const ctx = this.ctx;
    const bus = ctx.createGain();
    bus.gain.value = 0.55;
    bus.connect(out);
    const note = (m, t, d, type, vol) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = type;
      o.frequency.value = mtof(m);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(vol, t + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0001, t + d);
      o.connect(g).connect(bus);
      o.start(t);
      o.stop(t + d + 0.05);
    };
    const stepDur = 60 / p.bpm / 2; // 8분음표
    let next = ctx.currentTime + 0.15;
    let i = 0;
    timers.push(
      setInterval(() => {
        if (!this.ctx) return;
        while (next < ctx.currentTime + 0.35) {
          const chord = p.prog[Math.floor(i / 8) % p.prog.length];
          const s = i % 8;
          if (s === 0 || s === 4) note(p.root - 24 + chord[0], next, stepDur * 3, "triangle", 0.05);
          note(p.root + chord[s % chord.length] + (s >= 4 ? 12 : 0), next, stepDur * 0.8, "square", 0.0045);
          const m = p.melody[i % p.melody.length];
          if (m !== null) note(p.root + 12 + m, next, stepDur * 1.7, "triangle", 0.02);
          next += stepDur;
          i++;
        }
      }, 60)
    );
  }

  drop(out) {
    const ctx = this.ctx;
    if (!ctx) return;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(1800 + Math.random() * 2200, t);
    o.frequency.exponentialRampToValueAtTime(600, t + 0.05);
    g.gain.setValueAtTime(0.012 + Math.random() * 0.02, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
    o.connect(g).connect(out);
    o.start(t);
    o.stop(t + 0.07);
  }

  clink(out) {
    const ctx = this.ctx;
    if (!ctx) return;
    const t = ctx.currentTime;
    [2400, 3150].forEach((f) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = f + Math.random() * 200;
      g.gain.setValueAtTime(0.008, t);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
      o.connect(g).connect(out);
      o.start(t);
      o.stop(t + 0.4);
    });
  }

  /* ---------- 효과음 ---------- */

  tone(freq, dur, vol, type = "sine", when = 0) {
    if (!this.ctx) return;
    const t = this.ctx.currentTime + when;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(this.master);
    o.start(t);
    o.stop(t + dur + 0.02);
  }

  /** 타자 소리 (아주 작게). 픽셀 모드에서는 게임 대화창 '삐빅' */
  tick() {
    if (!this.ctx) return;
    if (this.chip) {
      this.tone(980 + Math.random() * 120, 0.035, 0.012, "square");
      return;
    }
    const t = this.ctx.currentTime;
    const src = this.ctx.createBufferSource();
    src.buffer = this.white;
    const f = this.ctx.createBiquadFilter();
    f.type = "bandpass";
    f.frequency.value = 2600 + Math.random() * 900;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.035, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.025);
    src.connect(f).connect(g).connect(this.master);
    src.start(t, Math.random());
    src.stop(t + 0.03);
  }

  heartbeat() {
    if (!this.ctx) return;
    [0, 0.22, 0.95, 1.17].forEach((w, i) => {
      this.thump(w, i % 2 ? 0.35 : 0.5);
    });
  }

  thump(when, vol) {
    if (!this.ctx) return;
    const t = this.ctx.currentTime + when;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.frequency.setValueAtTime(70, t);
    o.frequency.exponentialRampToValueAtTime(38, t + 0.16);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol, t + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
    o.connect(g).connect(this.master);
    o.start(t);
    o.stop(t + 0.25);
  }

  notify() {
    this.tone(1318.5, 0.25, 0.06);
    this.tone(1760, 0.4, 0.05, "sine", 0.12);
  }

  select() {
    this.tone(880, 0.35, 0.05, "triangle");
    this.tone(1320, 0.45, 0.025, "sine", 0.05);
  }

  whoosh() {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const src = this.ctx.createBufferSource();
    src.buffer = this.white;
    const f = this.ctx.createBiquadFilter();
    f.type = "bandpass";
    f.Q.value = 1.2;
    f.frequency.setValueAtTime(300, t);
    f.frequency.exponentialRampToValueAtTime(2400, t + 0.9);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.06, t + 0.4);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.1);
    src.connect(f).connect(g).connect(this.master);
    src.start(t);
    src.stop(t + 1.2);
  }
}
